import React, { useEffect, useState } from 'react';
import { getUserYouTubeMedia } from '../services/affiliateService';
import { FaEye, FaThumbsUp, FaComment, FaClock, FaCheckCircle, FaTimesCircle, FaFilter, FaTimes, FaList, FaChevronDown } from 'react-icons/fa';
import { ToastContainer, } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusIcons = {
  pending: FaClock,
  approved: FaCheckCircle,
  rejected: FaTimesCircle,
};

const FilterChip = ({ label, onRemove }) => (
  <div className="mb-2 mr-2 inline-flex items-center rounded-full bg-gray-100 px-3 py-1">
    <span className="text-xs text-gray-700 sm:text-sm">{label}</span>
    <button type="button" onClick={onRemove} className="ml-2 text-gray-500 hover:text-gray-700">
      <FaTimes size={12} />
    </button>
  </div>
);

const CampaignPopup = ({ campaigns, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-96 w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Campaigns</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
            <FaTimes size={16} />
          </button>
        </div>

        <div className="space-y-2">
          {campaigns.map((campaign, index) => (
            <div key={index} className="flex items-center justify-between rounded-xl bg-gray-50 p-3">
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-sm font-medium text-gray-900">{campaign.campaignName}</h4>
                <p className="text-xs text-gray-500">Status: {campaign.status}</p>
              </div>
              <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${statusColors[campaign.status]}`}>
                {campaign.status}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const FilterSection = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-3 text-sm font-semibold text-gray-800"
      >
        {title}
        <FaChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-2 pb-3">{children}</div>}
    </div>
  );
};


const YouTubeMediaDashboard = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const res = await getUserYouTubeMedia();
        if (res.success) {
          // Sort videos by creation date (latest first - stack structure)
          const sortedVideos = res.videos.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.publishedAt || 0);
            const dateB = new Date(b.createdAt || b.publishedAt || 0);
            return dateB - dateA;
          });
          setVideos(sortedVideos);
          setFilteredVideos(sortedVideos);
        } else {
          setError(res.message || 'Failed to fetch media.');
        }
      } catch (err) {
        setError('Failed to fetch media.');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);
  // Apply filters whenever videos, statusFilter, or campaignFilter changes
  useEffect(() => {
    let filtered = [...videos];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(video => {
        const overallStatus = getOverallStatus(video);
        return overallStatus.status === statusFilter;
      });
    }

    if (campaignFilter !== 'all') {
      filtered = filtered.filter(video => {
        if (!video.campaignApprovals || video.campaignApprovals.length === 0) {
          return false;
        }
        return video.campaignApprovals.some(approval =>
          approval.campaignName === campaignFilter
        );
      });
    }

    setFilteredVideos(filtered);
  }, [videos, statusFilter, campaignFilter]);

  const getOverallStatus = (video) => {
    if (!video.campaignApprovals || video.campaignApprovals.length === 0) {
      return { status: 'pending', text: 'No campaigns' };
    }

    const allApproved = video.campaignApprovals.every(approval => approval.status === 'approved');
    const anyRejected = video.campaignApprovals.some(approval => approval.status === 'rejected');
    const anyPending = video.campaignApprovals.some(approval => approval.status === 'pending');

    if (allApproved) {
      return { status: 'approved', text: 'All campaigns approved' };
    } else if (anyRejected) {
      return { status: 'rejected', text: 'Some campaigns rejected' };
    } else if (anyPending) {
      return { status: 'pending', text: 'Pending approval' };
    }

    return { status: 'pending', text: 'No campaigns' };
  };

  const handleFilterChange = (newStatus) => {
    setStatusFilter(newStatus);

    // Update active filters
    const newFilters = [...activeFilters];
    const existingFilterIndex = newFilters.findIndex(f => f.type === 'status');

    if (existingFilterIndex >= 0) {
      if (newStatus === 'all') {
        newFilters.splice(existingFilterIndex, 1);
      } else {
        newFilters[existingFilterIndex].value = newStatus;
      }
    } else if (newStatus !== 'all') {
      newFilters.push({ type: 'status', value: newStatus });
    }

    setActiveFilters(newFilters);
  };

  const handleCampaignFilterChange = (newCampaign) => {
    setCampaignFilter(newCampaign);

    // Update active filters
    const newFilters = [...activeFilters];
    const existingFilterIndex = newFilters.findIndex(f => f.type === 'campaign');

    if (existingFilterIndex >= 0) {
      if (newCampaign === 'all') {
        newFilters.splice(existingFilterIndex, 1);
      } else {
        newFilters[existingFilterIndex].value = newCampaign;
      }
    } else if (newCampaign !== 'all') {
      newFilters.push({ type: 'campaign', value: newCampaign });
    }

    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setCampaignFilter('all');
    setActiveFilters([]);
  };

  const getCampaignList = () => {
    const campaigns = new Set();
    videos.forEach(video => {
      if (video.campaignApprovals) {
        video.campaignApprovals.forEach(approval => {
          if (approval.campaignName) {
            campaigns.add(approval.campaignName);
          }
        });
      }
    });
    return Array.from(campaigns).sort();
  };

  const campaignList = getCampaignList();

  const openCampaignPopup = (video) => {
    setSelectedVideo(video);
    setShowCampaignPopup(true);
  };

  const closeCampaignPopup = () => {
    setShowCampaignPopup(false);
    setSelectedVideo(null);
  };

  return (
    <div className="w-full p-4 lg:p-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {loading && (
        <div className="flex min-h-[240px] items-center justify-center">
          <LoadingSpinner message="Fetching your YouTube Media..." />
        </div>
      )}

      {!loading && (
        <>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
          >
            ← Back
          </button>

          <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">YouTube Media Dashboard</h1>
                <p className="mt-1 text-sm text-gray-500">Track your YouTube media submissions and their approval status</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
                >
                  <FaFilter className="h-3.5 w-3.5" />
                  Filters
                  {activeFilters.length > 0 && (
                    <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {activeFilters.length}
                    </span>
                  )}
                </button>
                <span className="text-xs text-gray-500 sm:text-sm">
                  Showing {filteredVideos.length} of {videos.length}
                </span>
              </div>
            </div>

            {activeFilters.length > 0 && (
              <div className="mt-3 flex flex-wrap border-t border-gray-100 pt-3">
                {activeFilters.map((filter, index) => (
                  <FilterChip
                    key={index}
                    label={`${filter.type}: ${filter.value}`}
                    onRemove={() => {
                      if (filter.type === 'status') handleFilterChange('all');
                      else if (filter.type === 'campaign') handleCampaignFilterChange('all');
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">{error}</div>
          ) : filteredVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <FaFilter className="mb-3 h-10 w-10 text-gray-300" />
              <h3 className="text-base font-semibold text-gray-900">No media found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'all' && campaignFilter === 'all'
                  ? 'No YouTube videos found. Add some media to get started!'
                  : 'No media found with current filters. Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {filteredVideos.map((video, idx) => {
                const overallStatus = getOverallStatus(video);
                const StatusIcon = statusIcons[overallStatus.status];
                const campaignCount = video.campaignApprovals?.length || 0;

                return (
                  <div key={video.videoId || idx} className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md">
                    <div className="relative">
                      <img
                        src={video.thumbnail || 'https://via.placeholder.com/120x90?text=No+Image'}
                        alt={video.title}
                        className="h-44 w-full object-cover sm:h-48"
                      />
                      <div className="absolute right-2 top-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold sm:text-xs ${statusColors[overallStatus.status]}`}>
                          <StatusIcon className="h-3 w-3" />
                          {overallStatus.status}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <h2 className="mb-1 truncate text-sm font-semibold text-gray-900 sm:text-base" title={video.title}>{video.title}</h2>
                      <p className="mb-2 text-xs text-gray-500">{video.publishedAt ? new Date(video.publishedAt).toLocaleDateString() : 'N/A'}</p>
                      <div className="mb-2 flex items-center gap-3 text-xs text-gray-600 sm:text-sm">
                        <span className="inline-flex items-center gap-1"><FaEye /> {video.statistics?.viewCount || 0}</span>
                        <span className="inline-flex items-center gap-1"><FaThumbsUp /> {video.statistics?.likeCount || 0}</span>
                        <span className="inline-flex items-center gap-1"><FaComment /> {video.statistics?.commentCount || 0}</span>
                      </div>

                      {campaignCount > 0 && (
                        <button
                          type="button"
                          onClick={() => openCampaignPopup(video)}
                          className="mb-2 flex w-full items-center justify-between rounded-xl bg-gray-50 p-2 text-xs transition hover:bg-gray-100 sm:text-sm"
                        >
                          <span className="inline-flex items-center gap-2 text-gray-700">
                            <FaList className="text-gray-400" />
                            {campaignCount} Campaign{campaignCount !== 1 ? 's' : ''}
                          </span>
                          <FaChevronDown className="text-gray-400" />
                        </button>
                      )}

                      <p className="mb-3 text-xs text-gray-500">{overallStatus.text}</p>

                      <a
                        href={video.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto text-sm font-medium text-orange-600 transition hover:text-orange-700"
                      >
                        Watch on YouTube
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {filterOpen && (
        <div className="fixed inset-x-0 bottom-0 top-20 z-[130]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFilterOpen(false)} />
          <div className="absolute bottom-0 right-0 top-0 flex w-full flex-col bg-white shadow-xl sm:w-[380px]">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-bold text-gray-900 sm:text-base">Filters</h3>
              <div className="flex items-center gap-3">
                <button type="button" onClick={resetFilters} className="text-xs text-gray-500 transition hover:text-gray-700 sm:text-sm">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setFilterOpen(false)}
                  className="rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4">
              <FilterSection title="Status">
                {['pending', 'approved', 'rejected'].map((status) => (
                  <label key={status} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" checked={statusFilter === status} onChange={() => handleFilterChange(status)} />
                    <span className="capitalize">{status}</span>
                  </label>
                ))}
              </FilterSection>

              <FilterSection title="Campaign">
                {campaignList.map((campaign) => (
                  <label key={campaign} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="radio" checked={campaignFilter === campaign} onChange={() => handleCampaignFilterChange(campaign)} />
                    {campaign}
                  </label>
                ))}
              </FilterSection>
            </div>
          </div>
        </div>
      )}

      <CampaignPopup
        campaigns={selectedVideo?.campaignApprovals || []}
        isOpen={showCampaignPopup}
        onClose={closeCampaignPopup}
      />
    </div>
  );
};

export default YouTubeMediaDashboard; 